/* Copyright (C) Okahu Inc 2023-2024. All rights reserved. */

import React from 'react';
import { IPrompt } from 'types/prompt';

import {
  alwaysRenderStringFormatToUI,
  handlePromptDataVarient,
} from '@/lib/helper';

const parseInput = (input: any) => {
  if (!input) return '';

  try {
    if (typeof input === 'string') {
      const parsed = new Function(`return ${input}`)();
      return parsed;
    }
    return input;
  } catch {
    return typeof input === 'string' ? input : '';
  }
};

const MultiplePromptDisplay = ({
  inputData,
  isDetails,
}: {
  inputData: IPrompt;
  isDetails?: boolean;
}) => {
  const renderContent = (data: IPrompt) => {
    const currentInput = data?.['data.input'] ?? data?.input;
    if (!currentInput) return null;

    // If input.input is an array (conversation)
    if (Array.isArray(currentInput.input)) {
      const inputArray = currentInput.input;
      if (!inputArray.length) return null;

      const lastElement = parseInput(inputArray[inputArray.length - 1]);
      const lastQuestion =
        typeof lastElement === 'string'
          ? lastElement
          : lastElement?.[Object.keys(lastElement)[0]] || '';

      const exchanges = inputArray.map((item) => {
        const parsed = parseInput(item);
        return typeof parsed === 'string'
          ? { content: parsed }
          : {
              role: Object.keys(parsed)[0],
              content: parsed[Object.keys(parsed)[0]],
            };
      });

      return (
        <div className={`flex flex-col ${!isDetails && 'px-2 pb-3'}`}>
          <p className="mx-2 mb-3 mt-[2px] text-form-input-text text-body-primary">
            {/* Make sure always render string to the UI using alwaysRenderStringFormatToUI function. */}
            {alwaysRenderStringFormatToUI(lastQuestion)}
          </p>

          <div className="space-y-1 rounded bg-white/[8%] p-2">
            {exchanges.map((exchange, index) => {
              // Handle the display response.
              const displayContent = handlePromptDataVarient(exchange.content);

              const role = exchange.role;
              return (
                <div key={index}>
                  {exchange.role && (
                    <p className="text-body-sm capitalize text-form-input-disabled">
                      {role}
                    </p>
                  )}
                  <p className="mt-[2px] break-words break-all text-form-input-text text-body-primary">
                    {/* Make sure always render string to the UI using alwaysRenderStringFormatToUI function. */}
                    {alwaysRenderStringFormatToUI(displayContent)}
                  </p>
                </div>
              );
            })}
          </div>
        </div>
      );
    }

    // If input.input is a string
    if (typeof currentInput.input === 'string') {
      // Handle the case where the value might be an array or object
      const inputValue = data['data.input']?.input || data?.input?.input;
      const displayValue =
        typeof inputValue === 'string'
          ? inputValue
          : Array.isArray(inputValue)
            ? inputValue.join(', ')
            : JSON.stringify(inputValue);

      return (
        <div className={`flex flex-col ${!isDetails && 'px-2 pb-3'}`}>
          <p className="rounded bg-white/[8%] p-2 text-form-input-text">
            {alwaysRenderStringFormatToUI(displayValue)}
          </p>
        </div>
      );
    }

    if (typeof currentInput === 'object' && currentInput !== null) {
      if (currentInput.input === undefined) {
        return (
          <div className={`flex flex-col ${!isDetails && 'px-2 pb-3'}`}>
            <pre className="rounded bg-white/[8%] p-2 text-form-input-text">
              {JSON.stringify(currentInput, null, 2)}
            </pre>
          </div>
        );
      }
    }

    // Fixed: Handle the fallback case properly
    const questionValue = data['data.input']?.question || data?.input?.question;
    const displayQuestion =
      typeof questionValue === 'string'
        ? questionValue
        : Array.isArray(questionValue)
          ? (questionValue as string[])?.join(', ')
          : questionValue
            ? JSON.stringify(questionValue)
            : '';

    return (
      <p className={`${!isDetails && 'mx-4'} mt-1 text-body-primary`}>
        {alwaysRenderStringFormatToUI(displayQuestion)}
      </p>
    );
  };

  return renderContent(inputData);
};

export { MultiplePromptDisplay };
